<script>
import Vue from 'vue'
import { MODAL, RELOAD } from '@constants/Text'
import { GlobalBus } from '@services/bus'
import rules from '@constants/Rules'
import { apiCrmTranscricoes } from '@modules/crm/api/apiCrmTranscricoes'

const buildRow = () => ({ nome: '', email: '' })

export default {
  name: 'TranscriptParticipantsModal',
  data() {
    return {
      busModal: new Vue(),
      rules,
      id: null,
      rows: [buildRow()],
      loading: false,
    }
  },
  created() {
    GlobalBus.$on(MODAL.TRANSCRIPT_PARTICIPANTS, this.open)
  },
  beforeDestroy() {
    GlobalBus.$off(MODAL.TRANSCRIPT_PARTICIPANTS, this.open)
  },
  methods: {
    clear() {
      this.id = null
      this.rows = [buildRow()]
      this.loading = false
      if (this.$refs.form) this.$refs.form.resetValidation()
    },
    open({ id = null, participants = [], addNew = false } = {}) {
      this.clear()
      this.id = id || null
      const list = (participants || []).map((p) => ({
        nome: (p && (p.nome || p.name)) || '',
        email: (p && (p.email || p.e_mail)) || '',
      }))
      this.rows = list.length ? list : [buildRow()]
      this.busModal.$emit('dialog', true)
      // Aberto pelo bloco "Adicionar participante": já cria uma linha nova
      // (quando há participantes) e foca nela.
      if (addNew && list.length) {
        this.addRow()
      } else {
        this.focusRow(this.rows.length - 1)
      }
    },
    addRow() {
      this.rows.push(buildRow())
      this.focusRow(this.rows.length - 1)
    },
    // Foca o campo "Nome" da linha informada (cada linha tem 2 inputs).
    focusRow(index) {
      this.$nextTick(() => {
        const root = this.$refs.rows
        if (!root) return
        const inputs = root.querySelectorAll('input')
        const target = inputs[index * 2]
        if (target) target.focus()
      })
    },
    removeRow(index) {
      this.rows.splice(index, 1)
      if (!this.rows.length) this.rows = [buildRow()]
    },
    async save() {
      if (this.loading) return
      if (this.$refs.form && !this.$refs.form.validate()) return
      if (!this.id) return

      // Mantém só linhas com nome; e-mail é opcional (null quando vazio).
      const participants = this.rows
        .map((r) => ({
          nome: (r.nome || '').trim(),
          email: (r.email || '').trim() || null,
        }))
        .filter((r) => r.nome)

      this.loading = true
      try {
        const r = await apiCrmTranscricoes.atualizar({
          id: this.id,
          participants,
        })

        if (r && r.status === 200) {
          this.busModal.$emit('dialog', false)
          GlobalBus.$emit(RELOAD.TRANSCRIPT)
        }

        this.$showSnack(r)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<template>
  <BaseModal
    centerScreen
    :bus="busModal"
    persistent
    centerAndDecreasesPadding
    maxWidth="560px"
    backgroundColor="white"
  >
    <div class="pb-6">
      <div class="modal-title-container">
        <span class="modal-title">Editar participantes</span>
      </div>

      <div class="modal-content">
        <BaseValidationForm ref="form">
          <div ref="rows">
            <div
              v-for="(row, i) in rows"
              :key="i"
              class="d-flex align-center az-gap-small mb-2"
            >
              <InputByType
                v-model="row.nome"
                type="text"
                placeholder="Nome"
                outlined
                dense
                hide-details
                :rules="[rules.required]"
                class="flex-grow-1"
              />
              <InputByType
                v-model="row.email"
                type="text"
                placeholder="E-mail (opcional)"
                outlined
                dense
                hide-details
                class="flex-grow-1"
              />
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="removeRow(i)"
                  >
                    <icon-svg name="trash" :size="16" color="var(--muted)" />
                  </v-btn>
                </template>
                <span>Remover</span>
              </v-tooltip>
            </div>
          </div>

          <div
            class="transcript-participants-modal__add az-text-small mt-1"
            @click="addRow"
          >
            + Adicionar participante
          </div>
        </BaseValidationForm>
      </div>
    </div>

    <template v-slot:actions="{ close }">
      <!-- mt-4 no container: margem direta em BaseButton irmão é proibida. -->
      <div class="d-flex align-center justify-end az-gap-small mt-4">
        <BaseButton underlined @click="close"> Cancelar </BaseButton>
        <BaseButton primary :loading="loading" @click="save"> Salvar </BaseButton>
      </div>
    </template>
  </BaseModal>
</template>

<style lang="sass" scoped>
// Bloco pontilhado "adicionar" — mesmo padrão usado na aba de participantes.
.transcript-participants-modal__add
  display: flex
  align-items: center
  justify-content: center
  border: 1px dashed var(--gray-3, #C4C4C4)
  border-radius: 8px
  padding: 10px 12px
  cursor: pointer
  transition: background 0.2s

  &:hover
    background: #F5F6FA
</style>
